'use client';

import { FC, KeyboardEvent, ReactNode, useId, useState } from 'react';

import classNames from 'classnames';

import { Icon } from '@/components';

import styles from './Accordion.module.scss';

interface IAccordionProps {
  children: ReactNode;
  className?: string;
  classNameHeader?: string;
  haveIconBefore?: boolean;
  isExpanded?: boolean;
  iconExpanded?: string;
  iconCollapsed?: string;
  label: ReactNode;
}

const Accordion: FC<IAccordionProps> = ({
  label,
  isExpanded = false,
  children,
  haveIconBefore,
  className,
  classNameHeader,
  iconExpanded,
  iconCollapsed,
}) => {
  const [expanded, setExpanded] = useState(isExpanded);
  const panelId = useId();

  const toggleExpanded = () => setExpanded(prev => !prev);

  const onHeaderKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleExpanded();
    }
  };

  return (
    <section className={classNames(styles.accordion, className)}>
      <div
        aria-controls={panelId}
        aria-expanded={expanded}
        className={classNames(styles.accordion_header, classNameHeader, {
          [styles.prepended_icon]: haveIconBefore,
        })}
        onClick={toggleExpanded}
        onKeyDown={onHeaderKeyDown}
        role="button"
        tabIndex={0}
      >
        <div className={styles.accordion_title}>{label}</div>
        <Icon
          aria-hidden="true"
          className={styles.accordion_icon}
          name={
            expanded
              ? iconExpanded ?? 'angle-up'
              : iconCollapsed ?? 'angle-down'
          }
        />
      </div>
      <div
        aria-hidden={!expanded}
        className={classNames(styles.accordion_body, {
          [styles.accordion_body__collapsed]: !expanded,
        })}
        id={panelId}
        // Collapsed panels stay mounted for animation/state, but must leave
        // the tab order (max-height:0 alone does not).
        {...(!expanded ? { inert: true } : {})}
      >
        {children}
      </div>
    </section>
  );
};

export { Accordion };
